import { captureException } from '@sentry/react'
import { MultiviewFeedId } from './feedId'
import { MULTIVIEW_LIMITS, SESSION_COLORS } from '../config'

const FEED_SEPARATOR = '~'
const STORAGE_KEY_PREFIX = 'multiview:'

export interface MultiviewPersistedState {
  feeds: MultiviewFeedId[]
  colors: Record<string, number>
}

export const saveToSessionStorage = (
  orgId: string,
  state: MultiviewPersistedState
): void => {
  const key = `${STORAGE_KEY_PREFIX}${orgId}`
  const value = encodeMultiviewState(state)
  if (value) {
    sessionStorage.setItem(key, value)
  } else {
    sessionStorage.removeItem(key)
  }
}

export const loadFromSessionStorage = (orgId: string): MultiviewPersistedState => {
  const key = `${STORAGE_KEY_PREFIX}${orgId}`
  const value = sessionStorage.getItem(key)
  return parseMultiviewState(value ?? undefined)
}

export const parseMultiviewState = (
  feedsParam: string | undefined
): MultiviewPersistedState => {
  if (!feedsParam || feedsParam.trim() === '') {
    return { feeds: [], colors: {} }
  }

  try {
    const feeds: MultiviewFeedId[] = []
    const colors: Record<string, number> = {}
    const parts = feedsParam.split(',').filter(Boolean)

    for (const part of parts) {
      const trimmed = part.trim()
      const segments = trimmed.split(FEED_SEPARATOR)

      if (segments.length < 2) {
        continue
      }

      const sessionId = segments[0]
      const feedId = segments[1]
      const colorIndex = segments[2] !== undefined ? parseInt(segments[2], 10) : undefined

      if (sessionId && feedId) {
        feeds.push(`${sessionId}:${feedId}`)

        if (colorIndex !== undefined && !isNaN(colorIndex) && colorIndex >= 0 && colorIndex < SESSION_COLORS.length) {
          colors[sessionId] = colorIndex
        }
      }

      if (feeds.length >= MULTIVIEW_LIMITS.maxFeeds) {
        break
      }
    }

    return { feeds, colors }
  } catch (error) {
    captureException(error, {
      tags: { feature: 'multiview', action: 'parseState' },
      extra: { feedsParam },
    })
    return { feeds: [], colors: {} }
  }
}

export const encodeMultiviewState = (state: MultiviewPersistedState): string => {
  const { feeds, colors } = state
  if (!feeds || feeds.length === 0) {
    return ''
  }

  return feeds
    .map((feedId) => {
      const colonIndex = feedId.indexOf(':')
      if (colonIndex === -1) return feedId
      const sessionId = feedId.slice(0, colonIndex)
      const streamId = feedId.slice(colonIndex + 1)
      const colorIndex = colors[sessionId]

      if (colorIndex !== undefined) {
        return `${sessionId}${FEED_SEPARATOR}${streamId}${FEED_SEPARATOR}${colorIndex}`
      }
      return `${sessionId}${FEED_SEPARATOR}${streamId}`
    })
    .join(',')
}
